// FILE: src/utils/firebase/features/transactions/fetchers.js
import {
  getDbOrThrow,
  DEFAULT_CAP,
  normalizeEmail,
  toQueryDate,
  mapRawDocs,     // still used elsewhere, but NOT inside the search fetcher now
  col,
} from "../../core/firestoreCore";
import { COLLECTIONS } from "../../core/collections";
import {
  getDocs,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  startAt,
  endAt,
} from "firebase/firestore";
import {
  fetchByEmailOnce as _fetchByEmailOnce,
  subscribeByEmailHelper as _subByEmail,
} from "../../core/emailScoped";

import { getUserTransactionsCached } from "../../cache/transactionsCached";

/* ---------------- Subscriptions ---------------- */

export function subscribeUserTransactions({ userEmail, cap = DEFAULT_CAP, onData, onError }) {
  return _subByEmail({
    colName: COLLECTIONS.userTransactions,
    userEmail,
    cap,
    extra: [],
    onData,
    onError,
  });
}

/* ---------------- One-shot fetches ---------------- */

export function fetchUserTransactionsOnce(userEmail, opts = {}) {
  // identical to legacy: cap default from DEFAULT_CAP there
  const cap = opts.cap ?? DEFAULT_CAP;
  return _fetchByEmailOnce(COLLECTIONS.userTransactions, userEmail, { ...opts, cap })
    .then(mapRawDocs);
}

export async function fetchUserTransactionsRange({
  userEmail,
  fromDate,
  toDate,
  cap = 25,
  latestFirst = true,
} = {}) {
  const db = getDbOrThrow();
  const email = normalizeEmail(userEmail);
  if (!email) throw new Error("fetchUserTransactionsRange: missing userEmail");

  const base = col(db, COLLECTIONS.userTransactions);
  const constraints = [where("userEmail", "==", email)];

  const fromQ = toQueryDate(fromDate);
  const toQ = toQueryDate(toDate);
  if (fromQ !== undefined) constraints.push(where("date", ">=", fromQ));
  if (toQ !== undefined) constraints.push(where("date", "<=", toQ));

  constraints.push(orderBy("date", latestFirst ? "desc" : "asc"));
  constraints.push(limit(Math.max(1, cap)));

  const qy = query(base, ...constraints);
  const snap = await getDocs(qy);
  return mapRawDocs(snap);
}

export async function pageUserTransactionsAfterDate({
  userEmail,
  afterDate,
  pageSize = 25,
  latestFirst = true,
} = {}) {
  const db = getDbOrThrow();
  const email = normalizeEmail(userEmail);
  if (!email) throw new Error("pageUserTransactionsAfterDate: missing userEmail");

  const base = col(db, COLLECTIONS.userTransactions);
  const constraints = [where("userEmail", "==", email)];

  const afterQ = toQueryDate(afterDate);
  const dir = latestFirst ? "desc" : "asc";
  constraints.push(orderBy("date", dir));
  if (afterQ !== undefined) constraints.push(startAfter(afterQ));
  constraints.push(limit(Math.max(1, pageSize)));

  const qy = query(base, ...constraints);
  const snap = await getDocs(qy);
  return mapRawDocs(snap);
}

/* ---------------- Delta fetch (for cache refresh) ---------------- */
/**
 * Fetch only transactions updated after a given timestamp.
 * sinceUpdatedAt: number | Date (ms or Date). Firestore will coerce Date → Timestamp.
 * Returns mapped docs (array).
 */
export async function fetchUserTransactionsDelta({
  userEmail,
  sinceUpdatedAt, // ms or Date
  cap = DEFAULT_CAP,
  latestFirst = true,
} = {}) {
  const db = getDbOrThrow();
  const email = normalizeEmail(userEmail);
  if (!email) throw new Error("fetchUserTransactionsDelta: missing userEmail");

  const cutoff =
    sinceUpdatedAt instanceof Date ? sinceUpdatedAt :
    (Number.isFinite(sinceUpdatedAt) ? new Date(Number(sinceUpdatedAt)) : null);

  if (!cutoff) {
    // Fallback: no cutoff -> fetch a minimal recent slice by date
    return fetchUserTransactionsRange({ userEmail, cap, latestFirst });
  }

  const base = col(db, COLLECTIONS.userTransactions);
  const dir = latestFirst ? "desc" : "asc";

  // Query by updatedAt; Firestore allows Date for Timestamp field comparisons.
  const qy = query(
    base,
    where("userEmail", "==", email),
    where("updatedAt", ">", cutoff),
    orderBy("updatedAt", dir),
    limit(Math.max(1, cap))
  );
  const snap = await getDocs(qy);
  return mapRawDocs(snap);
}

/* ---------------- Description prefix search (DB-first with safe mapping) ---------------- */
/**
 * Search user transactions by description prefix (case-insensitive).
 * Primary path: Firestore index on "descriptionLower" with userEmail equality.
 * Fallback path: fetch a slice and filter on the client.
 *
 * This implementation avoids calling mapRawDocs to prevent shape/arity issues.
 * It hard-guards all shapes and ALWAYS returns an array.
 */
export async function searchUserTransactionsByDescriptionPrefix({
  userEmail,
  text,
  cap = 100,
  latestFirst = true,
} = {}) {
  const db = getDbOrThrow();
  const email = normalizeEmail(userEmail);
  if (!email) throw new Error("searchUserTransactionsByDescriptionPrefix: missing userEmail");

  const qtext = String(text || "").trim().toLowerCase();
  if (!qtext) return [];

  // Local helpers (mirror app sort: date desc → updated desc → created desc → id desc)
  const toMs = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === "function") { try { return v.toMillis(); } catch { /* noop */ } }
    if (v instanceof Date) return v.getTime();
    const p = Date.parse(v);
    return Number.isFinite(p) ? p : 0;
  };
  const sortRows = (rows) => {
    const copy = Array.isArray(rows) ? rows.slice() : [];
    copy.sort((a, b) => {
      const ad = Date.parse(a?.date) || 0;
      const bd = Date.parse(b?.date) || 0;
      if (ad !== bd) return bd - ad;
      const au = toMs(a?.updatedAt);
      const bu = toMs(b?.updatedAt);
      if (au !== bu) return bu - au;
      const ac = toMs(a?.createdAt);
      const bc = toMs(b?.createdAt);
      if (ac !== bc) return bc - ac;
      return String(b?.id || "").localeCompare(String(a?.id || ""));
    });
    return copy;
  };

  try {
    const base = col(db, COLLECTIONS.userTransactions);
    // Requires a composite index: userEmail ==, orderBy(descriptionLower)
    const qy = query(
      base,
      where("userEmail", "==", email),
      orderBy("descriptionLower"),
      startAt(qtext),
      endAt(qtext + "\uf8ff"),
      limit(Math.max(1, cap))
    );

    const snap = await getDocs(qy);

    // 🔒 Safe, self-contained mapping — never calls mapRawDocs
    const rows = Array.isArray(snap?.docs)
      ? snap.docs.map((d) => {
          const data = typeof d.data === "function" ? d.data() : {};
          return { id: d.id, ...data };
        })
      : [];

    return sortRows(rows);
  } catch (err) {
    // Fallback: fetch a recent slice and filter client-side (always coerced to array)
    let slice = [];
    try {
      const res = await fetchUserTransactionsOnce(userEmail, { cap: 1000, latestFirst });
      slice = Array.isArray(res) ? res : [];
    } catch {
      slice = [];
    }

    const filtered = slice.filter((r) =>
      String(r?.description || "").toLowerCase().includes(qtext)
    );
    return sortRows(filtered).slice(0, cap);
  }
}

/**
 * fetchPlannedTransactions (Option A — cached)
 * A "planned" transaction is simply a normal transaction whose date is in the future.
 * This helper reuses the cached range fetch to get transactions between [fromDate, toDate]
 * and returns them as a plain array. Sorting is normalized by date.
 *
 * @param {Object} opts
 * @param {string} opts.userEmail
 * @param {string} [opts.fromDate]  ISO "YYYY-MM-DD" (defaults to today)
 * @param {string} [opts.toDate]    ISO "YYYY-MM-DD" (defaults to today + 365 days)
 * @param {number} [opts.cap=1000]
 * @param {boolean} [opts.latestFirst=false]  order by date (desc if true, asc if false)
 * @returns {Promise<Array>} array of transactions
 */
export async function fetchPlannedTransactions({
  userEmail,
  fromDate,
  toDate,
  cap = 1000,
  latestFirst = false,
} = {}) {
  // defaults: today → +1 year
  const today = new Date();
  const toISO = (d) => {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const dd = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const defaultFrom = toISO(today);
  const plusDays = (d, n) => {
    const t = new Date(d);
    t.setDate(t.getDate() + n);
    return t;
  };
  const from = String(fromDate || defaultFrom);
  const to = String(toDate || toISO(plusDays(defaultFrom, 365)));

  // Reuse cached range fetch (no new query path / index)
  // getUserTransactionsCached is used elsewhere in-app; it returns either
  // an array or an object with `.rows`. Normalize to array.
  let res = [];
  try {
    const out = await getUserTransactionsCached({
      userEmail,
      fromDate: from,
      toDate: to,
      cap,
      latestFirst, // many call sites already rely on this field
    });

    if (Array.isArray(out)) {
      res = out;
    } else if (out && Array.isArray(out.rows)) {
      res = out.rows;
    } else if (out && Array.isArray(out.items)) {
      res = out.items;
    } else {
      res = [];
    }
  } catch {
    res = [];
  }

  // Normalize sort by date to honor latestFirst consistently.
  const dir = latestFirst ? -1 : 1;
  const toTs = (v) => {
    // Accept Firestore Timestamp, ISO string, Date
    if (v && typeof v.toDate === "function") {
      try { return v.toDate().getTime(); } catch { /* ignore */ }
    }
    if (v instanceof Date) return v.getTime();
    const p = Date.parse(v);
    return Number.isFinite(p) ? p : 0;
    };
  res.sort((a, b) => (toTs(a?.date) - toTs(b?.date)) * dir);

  return res;
}
